import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import { MenuItem, Select } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { updateUser } from "../../../../../utils/Apis";

const SOCIAL_PLATFORMS = [
  "Facebook",
  "Twitter",
  "Instagram",
  "LinkedIn",
  "YouTube",
  "GitHub",
  "GitLab",
  "Bitbucket",
  "Reddit",
  "Discord",
  "Stack Overflow",
  "Medium",
  "Pinterest",
  "Twitch",
  "Dribbble",
];

const SettingsTab = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.data);
  const loading = useSelector((s) => s.user.loading);
  const error = useSelector((s) => s.user.error);
  const branches = useSelector((s) => s.data.branches.branches) || [];

  const [data, setData] = useState({});
  const [profilePic, setProfilePic] = useState("");
  const [notification, setNotification] = useState({});
  const [errors, setErrors] = useState({});
  const [warningOpen, setWarningOpen] = useState(false);
  const [originalData, setOriginalData] = useState({});

  const isSocialLinksEmpty = (arr) =>
    !Array.isArray(arr) ||
    arr.length === 0 ||
    arr.every(
      (s) =>
        (!s.platform || typeof s.platform !== "string" || !s.platform.trim()) &&
        (!s.link || typeof s.link !== "string" || !s.link.trim())
    );

  useEffect(() => {
    if (!user) return;

    const formattedData = {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      fatherName: user.fatherName || "",
      motherName: user.motherName || "",
      dob: user.dob ? dayjs(user.dob).format("YYYY-MM-DD") : "",
      email: user.email || "",
      mobileNo: user.mobileNo || "",
      street: user.addressDetails?.street || "",
      city: user.addressDetails?.city || "",
      state: user.addressDetails?.state || "",
      zipCode: user.addressDetails?.zipCode || "",
      socialLinks: user.socialLinks?.length ? user.socialLinks : [],
      batchFrom: user.studentDetails?.batchPeriod?.from
        ? dayjs(user.studentDetails.batchPeriod.from).format("YYYY-MM-DD")
        : "",
      batchTo: user.studentDetails?.batchPeriod?.to
        ? dayjs(user.studentDetails.batchPeriod.to).format("YYYY-MM-DD")
        : "",
      feePaid: user.studentDetails?.feePaid || 0,
      feeTotal: user.studentDetails?.feeTotal || 0,
      feeDue: user.studentDetails?.feeDue || 0,
      discount: user.studentDetails?.userDiscount || 0,
      staffExpertise: user.staffDetails?.expertise || [],
      staffStatus: user.staffDetails?.status || "",
      adminPermissions: user.adminPermissions || [],
      studentStatus: user.studentDetails?.status || "",
    };

    setData(formattedData);
    setOriginalData(formattedData);
    setProfilePic(user.profilePic || "");
  }, [user]);

  const isDataChanged = () => {
    const clean = (val) =>
      typeof val === "string"
        ? val.trim()
        : Array.isArray(val)
          ? JSON.stringify(val)
          : val;

    const keysToCheck = Object.keys(originalData);

    for (let key of keysToCheck) {
      if (clean(data[key]) !== clean(originalData[key])) return true;
    }

    if (user.profilePic !== profilePic) return true;

    return false;
  };

  const handleChange = (e) => {
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setProfilePic(preview);
      // Optional: add upload API call here
    }
  };

  const handleSocialChange = (idx, k, v) => {
    const arr = [...data.socialLinks];
    arr[idx][k] = v;
    setData((prev) => ({ ...prev, socialLinks: arr }));
  };

  const addSocial = () =>
    setData((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, { platform: "", link: "" }],
    }));

  const removeSocial = (idx) =>
    setData((prev) => {
      const arr = prev.socialLinks.filter((_, i) => i !== idx);
      return {
        ...prev,
        socialLinks: arr.length ? arr : [{ platform: "", link: "" }],
      };
    });

  const validate = () => {
    const errs = {};
    if (!data.firstName.trim()) errs.firstName = "First name is required";
    if (!data.lastName.trim()) errs.lastName = "Last name is required";
    if (!/^\S+@\S+\.\S+$/.test(data.email)) errs.email = "Invalid email format";
    if (!/^\d{10}$/.test(data.mobileNo))
      errs.mobileNo = "Enter a valid 10-digit mobile number";
    if (
      user.role === "student" &&
      data.batchFrom &&
      data.batchTo &&
      dayjs(data.batchFrom).isAfter(dayjs(data.batchTo))
    )
      errs.batchPeriod = "Batch start must be before end";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      setNotification({
        type: "error",
        message: "Please correct the highlighted fields.",
      });
      return;
    }

    if (!isDataChanged()) {
      setNotification({
        type: "info",
        message: "No changes detected. Update not required.",
      });
      return;
    }

    if (
      data.email.toLowerCase() !== user.email ||
      data.mobileNo !== user.mobileNo
    ) {
      setWarningOpen(true);
    } else {
      submitData();
    }
  };
  const submitData = () => {
    const updatedFields = {};

    // Deep diff utility function to compare nested objects/arrays
    const findUpdatedFields = (current, original) => {
      const changes = {};

      Object.keys(current).forEach((key) => {
        const curVal = current[key];
        const origVal = original?.[key];

        if (key === "socialLinks") {
          if (isSocialLinksEmpty(curVal) && isSocialLinksEmpty(origVal)) {
            // both empty, no changes
          } else if (
            JSON.stringify(
              curVal.filter((s) => s.platform.trim() || s.link.trim())
            ) !==
            JSON.stringify(
              origVal
                ? origVal.filter((s) => s.platform.trim() || s.link.trim())
                : []
            )
          ) {
            curVal.filter(
              (s) =>
                (s.platform &&
                  typeof s.platform === "string" &&
                  s.platform.trim()) ||
                (s.link && typeof s.link === "string" && s.link.trim())
            );
          }
        }

        if (Array.isArray(curVal)) {
          // If original is missing or empty array, send current array directly as changed
          if (!origVal || origVal.length === 0) {
            if (curVal.length > 0) changes[key] = curVal;
          } else {
            // Compare normalized stringified arrays
            const normCur = JSON.stringify(
              curVal.map((i) => ({
                platform:
                  typeof i.platform === "string" ? i.platform.trim() : "",
                link: typeof i.link === "string" ? i.link.trim() : "",
              }))
            );
            const normOrig = JSON.stringify(
              Array.isArray(origVal)
                ? origVal.map((i) => ({
                    platform:
                      typeof i.platform === "string" ? i.platform.trim() : "",
                    link: typeof i.link === "string" ? i.link.trim() : "",
                  }))
                : []
            );
            if (normCur !== normOrig) {
              changes[key] = curVal;
            }
          }
        } else if (curVal && typeof curVal === "object") {
          const nestedChanges = findUpdatedFields(curVal, origVal || {});
          if (Object.keys(nestedChanges).length > 0) {
            changes[key] = nestedChanges;
          }
        } else {
          const cur = typeof curVal === "string" ? curVal.trim() : curVal;
          const orig = typeof origVal === "string" ? origVal.trim() : origVal;
          if (cur !== orig) {
            changes[key] = key === "email" ? cur.toLowerCase() : cur;
          }
        }
      });

      return changes;
    };

    // Main logic
    const basicChanges = findUpdatedFields(data, originalData);
    Object.assign(updatedFields, basicChanges);

    // Group addressDetails into a nested object
    const addressDetailsFields = ["street", "city", "state", "zipCode"];
    const addressDetails = {};

    addressDetailsFields.forEach((key) => {
      if (data[key] !== originalData[key]) {
        addressDetails[key] = data[key];
      }
    });

    if (Object.keys(addressDetails).length > 0) {
      updatedFields.addressDetails = addressDetails;

      // Optional: remove flat keys if already added
      addressDetailsFields.forEach((key) => {
        delete updatedFields[key];
      });
    }

    // Check for profile picture change
    if (user.profilePic !== profilePic) {
      updatedFields.profilePic = profilePic;
      updatedFields.oldProfilePic = user.profilePic; // for deletion on backend
    }

    // Handle role-based nested structures
    if (user.role === "student") {
      const studentFields = {
        batchPeriod: {
          from: new Date(data.batchFrom),
          to: new Date(data.batchTo),
        },
        courses: data.studentCourses,
        feePaid: data.feePaid,
        feeTotal: data.feeTotal,
        feeDue: data.feeDue,
        userDiscount: data.discount,
        status: data.studentStatus,
      };
      const originalStudent = originalData.studentDetails || {};
      const changedStudentFields = findUpdatedFields(
        studentFields,
        originalStudent
      );
      if (Object.keys(changedStudentFields).length > 0) {
        updatedFields.studentDetails = changedStudentFields;
      }
    }

    if (user.role === "staff") {
      const staffFields = {
        expertise: data.staffExpertise,
        assignedCourses: data.staffCourses,
        shift: data.staffShift,
        status: data.staffStatus,
      };
      const originalStaff = originalData.staffDetails || {};
      const changedStaffFields = findUpdatedFields(staffFields, originalStaff);
      if (Object.keys(changedStaffFields).length > 0) {
        updatedFields.staffDetails = changedStaffFields;
      }
    }

    if (user.role === "admin") {
      const originalPerms = originalData.adminPermissions || [];
      if (
        JSON.stringify(data.adminPermissions) !== JSON.stringify(originalPerms)
      ) {
        updatedFields.adminPermissions = data.adminPermissions;
      }
    }

    // Dispatch update
    dispatch(updateUser({ id: user._id, formData: updatedFields }))
      .unwrap()
      .then((res) => {
        setNotification({
          type: "success",
          message: res.message || "Profile updated successfully.",
        });
        setWarningOpen(false);
      })
      .catch((err) =>
        setNotification({
          type: "error",
          message: err?.message || "Failed to update profile.",
        })
      );
  };

  const branchName =
    branches.find((b) => b._id === (user.branchId?._id || user.branchId))
      ?.name || "N/A";

  if (loading || !data.firstName) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
      {/* Avatar and Name Section - from your original (second) codebase */}
      <Box display="flex" alignItems="center" gap={3} mb={4}>
        <Box position="relative">
          <Avatar src={profilePic} sx={{ width: 100, height: 100 }} />
          <IconButton
            component="label"
            sx={{
              position: "absolute",
              bottom: -8,
              right: -8,
              //   bgcolor: "#fff",
              border: "1px solid #ccc",
              boxShadow: 1,
            }}
          >
            <PhotoCamera fontSize="small" />
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleProfilePicChange}
            />
          </IconButton>
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {data.firstName} {data.lastName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user.role?.toUpperCase()} | ID: {user.UserId}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Branch: {branchName}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Basic Fields */}
      <Grid container spacing={2} mb={3}>
        {[
          ["firstName", "First Name"],
          ["lastName", "Last Name"],
          ["fatherName", "Father's Name"],
          ["motherName", "Mother's Name"],
        ].map(([k, label]) => (
          <Grid item xs={12} md={6} key={k}>
            <TextField
              label={label}
              name={k}
              fullWidth
              value={data[k]}
              onChange={handleChange}
              error={!!errors[k]}
              helperText={errors[k]}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={6}>
          <TextField
            label="Date of Birth"
            name="dob"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={data.dob}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      {/* Contact Info */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Contact Information
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            name="email"
            fullWidth
            value={data.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Mobile Number"
            name="mobileNo"
            fullWidth
            value={data.mobileNo}
            onChange={handleChange}
            error={!!errors.mobileNo}
            helperText={errors.mobileNo}
          />
        </Grid>
      </Grid>

      {/* Address */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Address Details
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {["street", "city", "state", "zipCode"].map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k}>
            <TextField
              fullWidth
              label={k.charAt(0).toUpperCase() + k.slice(1)}
              name={k}
              value={data[k]}
              onChange={handleChange}
            />
          </Grid>
        ))}
      </Grid>

      {/* Social Links */}
      <Box mt={4}>
        <Typography variant="subtitle1">Social Links</Typography>
        {data.socialLinks.map((sl, idx) => (
          <Grid
            container
            spacing={1}
            alignItems="center"
            sx={{ my: 1 }}
            key={idx}
          >
            <Grid item xs={5}>
              <Select
                fullWidth
                value={sl.platform}
                onChange={(e) =>
                  handleSocialChange(idx, "platform", e.target.value)
                }
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Platform
                </MenuItem>
                {SOCIAL_PLATFORMS.filter(
                  (platform) =>
                    !data.socialLinks.some(
                      (s, i) => s.platform === platform && i !== idx
                    )
                ).map((platform) => (
                  <MenuItem key={platform} value={platform}>
                    {platform}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="URL"
                fullWidth
                value={sl.link}
                onChange={(e) =>
                  handleSocialChange(idx, "link", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <Button onClick={() => removeSocial(idx)}>Remove</Button>
            </Grid>
          </Grid>
        ))}
        {data.socialLinks.length < SOCIAL_PLATFORMS.length && (
          <Button onClick={addSocial}>Add Social Link</Button>
        )}
      </Box>

      {/* Save Button */}
      <Box textAlign="right" mt={4}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
        >
          Save Changes
        </Button>
      </Box>

      {/* Dialog for email/phone warning */}
      <Dialog open={warningOpen} onClose={() => setWarningOpen(false)}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing email or phone number cannot be undone. Proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningOpen(false)}>Cancel</Button>
          <Button color="error" onClick={submitData}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification.message}
        autoHideDuration={5000}
        onClose={() => setNotification({})}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={notification.type || "info"}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SettingsTab;
